import { useState, useEffect } from 'react'
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react'
import { productApi, CategoryNode } from '../../services/api'

interface FilterPanelProps {
  isOpen: boolean
  onClose: () => void
  selectedTagIds: string[]
  onApply: (tagIds: string[], attrs?: Record<string, any>) => void
  selectedCountry?: string | null
  selectedCity?: string | null
  userHomeCity?: { country: string | null; city: string | null }
  onLocationChange?: (country: string | null, city: string | null) => void
}

export default function FilterPanel({
  isOpen,
  onClose,
  selectedTagIds: initialTagIds,
  onApply,
}: FilterPanelProps) {
  const [tree, setTree] = useState<CategoryNode[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(initialTagIds))
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const [attrValues, setAttrValues] = useState<Record<string, any>>({})
  const [showAttributes, setShowAttributes] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    // Sync internal selection with parent state when panel opens.
    // The component stays mounted (returns null when closed) so without
    // this, selections cleared from the main page would still show here.
    setSelectedIds(new Set(initialTagIds))
    setAttrValues({})
    loadTree()
  }, [isOpen])

  const loadTree = async () => {
    try {
      setLoading(true)
      const data = await productApi.getCategoryTree('en')
      setTree(data.categories || [])
    } catch (err) {
      console.error('Failed to load category tree:', err)
    } finally {
      setLoading(false)
    }
  }

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelect = (id: string, node: CategoryNode) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
        // Also deselect all children
        const deselectChildren = (n: CategoryNode) => {
          next.delete(n.id)
          n.children?.forEach(deselectChildren)
        }
        node.children?.forEach(deselectChildren)
      } else {
        next.add(id)
        // Select all children for convenience
        const selectChildren = (n: CategoryNode) => {
          next.add(n.id)
          n.children?.forEach(selectChildren)
        }
        node.children?.forEach(selectChildren)
      }
      return next
    })
  }

  const handleApply = () => {
    // Only send attribute filters for attributes of currently-selected categories.
    const relevantKeys = new Set(selectedWithAttrs.flatMap(n => (n.attributes || []).map(a => a.key)))
    const attrs = Object.fromEntries(
      Object.entries(attrValues).filter(([k]) => relevantKeys.has(k))
    )
    onApply(Array.from(selectedIds), attrs)
    onClose()
  }

  // Find selected category with attributes
  const findNode = (nodes: CategoryNode[], id: string): CategoryNode | null => {
    for (const n of nodes) {
      if (n.id === id) return n
      if (n.children) {
        const found = findNode(n.children, id)
        if (found) return found
      }
    }
    return null
  }

  const selectedNodes = Array.from(selectedIds)
    .map(id => findNode(tree, id))
    .filter(Boolean) as CategoryNode[]

  const selectedWithAttrs = selectedNodes.filter(n => n.attributes?.length)

  const handleClear = () => {
    setSelectedIds(new Set())
    setSearch('')
    setAttrValues({})
  }

  const filterTree = (nodes: CategoryNode[], query: string): CategoryNode[] => {
    if (!query) return nodes
    const q = query.toLowerCase()
    return nodes.reduce<CategoryNode[]>((acc, node) => {
      const matches = node.name.toLowerCase().includes(q)
      const filteredChildren = node.children ? filterTree(node.children, query) : undefined
      if (matches || (filteredChildren && filteredChildren.length > 0)) {
        acc.push({ ...node, children: filteredChildren })
      }
      return acc
    }, [])
  }

  const renderNode = (node: CategoryNode, depth: number = 0) => {
    const isExpanded = expandedIds.has(node.id)
    const isSelected = selectedIds.has(node.id)
    const hasChildren = node.children && node.children.length > 0
    const isPartiallySelected = !isSelected && hasChildren && node.children?.some(c => selectedIds.has(c.id))

    return (
      <div key={node.id}>
        {/* Category row */}
        <div
          className="flex items-center gap-2 py-2.5 px-2 hover:bg-gray-50 rounded-lg cursor-pointer
            transition-colors group"
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
        >
          {/* Expand/collapse arrow */}
          <button
            onClick={(e) => { e.stopPropagation(); toggleExpand(node.id) }}
            className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-gray-400
              hover:text-gray-600 transition-colors"
          >
            {hasChildren ? (
              isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
            ) : (
              <span className="w-4" />
            )}
          </button>

          {/* Icon */}
          {node.icon && (
            <span className="text-base flex-shrink-0">{node.icon}</span>
          )}

          {/* Name + count */}
          <div className="flex-1 min-w-0" onClick={() => toggleSelect(node.id, node)}>
            <div className="flex items-center gap-2">
              <span className={`text-sm truncate transition-colors ${
                isSelected ? 'text-blue-600 font-semibold' : 'text-gray-700'
              }`}>
                {node.name}
              </span>
              {node.product_count > 0 && (
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {node.product_count}
                </span>
              )}
            </div>
          </div>

          {/* Checkbox */}
          <button
            onClick={(e) => { e.stopPropagation(); toggleSelect(node.id, node) }}
            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center
              flex-shrink-0 transition-all ${
                isSelected
                  ? 'bg-blue-500 border-blue-500 text-white'
                  : isPartiallySelected
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-400'
              }`}
          >
            {isSelected && <CheckMini />}
            {isPartiallySelected && <div className="w-2 h-2 bg-blue-500 rounded-sm" />}
          </button>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="border-l border-gray-100 ml-5">
            {node.children!.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  const filtered = filterTree(tree, search)
  const totalSelected = selectedIds.size

  // Render attribute filter based on type
  const renderAttribute = (attr: CategoryNode['attributes'] extends (infer T)[] | undefined ? T : never) => {
    const value = attrValues[attr.key]
    const setValue = (v: any) => setAttrValues((prev: Record<string, any>) => ({ ...prev, [attr.key]: v }))
    const label = typeof attr.label === 'object' ? attr.label.en : (attr.label || attr.key)

    return (
      <div key={attr.key} className="mb-3">
        <label className="block text-xs font-medium text-gray-600 mb-1.5">
          {label}{attr.unit ? ` (${attr.unit})` : ''}
        </label>

        {attr.type === 'enum' && attr.options ? (
          <div className="flex flex-wrap gap-1.5">
            {attr.options.map((opt: { v: string; en?: string; ru?: string; label?: string }) => {
              const isActive = Array.isArray(value) ? value.includes(opt.v) : value === opt.v
              return (
                <button
                  key={opt.v}
                  onClick={() => {
                    if (Array.isArray(value)) {
                      setValue(isActive ? value.filter((v: string) => v !== opt.v) : [...value, opt.v])
                    } else {
                      setValue(isActive ? undefined : opt.v)
                    }
                  }}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    isActive
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {opt.en || opt.label || opt.v}
                </button>
              )
            })}
          </div>
        ) : attr.type === 'int' || attr.type === 'decimal' ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={typeof value === 'object' ? (value?.min || '') : (value || '')}
              onChange={e => setValue({ min: e.target.value || undefined, max: typeof value === 'object' ? value?.max : undefined })}
              placeholder="Min"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-400"
              step={attr.type === 'decimal' ? '0.1' : '1'}
            />
            <span className="text-gray-400 text-xs">–</span>
            <input
              type="number"
              value={typeof value === 'object' ? (value?.max || '') : ''}
              onChange={e => setValue({ min: typeof value === 'object' ? value?.min : value, max: e.target.value || undefined })}
              placeholder="Max"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-400"
              step={attr.type === 'decimal' ? '0.1' : '1'}
            />
          </div>
        ) : attr.type === 'bool' ? (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!value}
              onChange={e => setValue(e.target.checked || undefined)}
              className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-400"
            />
            <span className="text-sm text-gray-700">{label}</span>
          </label>
        ) : (
          <input
            type="text"
            value={value || ''}
            onChange={e => setValue(e.target.value || undefined)}
            placeholder={`Enter ${label.toLowerCase()}`}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm
              focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        )}
      </div>
    )
  }

  if (!isOpen) return null

  return (
    // z-[60]: must paint above the app-level BottomNav (z-50), which otherwise
    // covers the Apply bar at the bottom of this panel.
    <div className="fixed inset-0 z-[60] flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-sm ml-auto bg-white h-full flex flex-col shadow-2xl
        animate-slide-in-right">
        {/* Header: title — Clear — Apply — close. Apply lives here (not in a bottom
            bar) so it can never be cut off or covered at the bottom of the viewport. */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-800 flex-1">Categories</h2>
          {totalSelected > 0 && (
            <button
              onClick={handleClear}
              className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              Clear ({totalSelected})
            </button>
          )}
          <button
            onClick={handleApply}
            className="bg-blue-500 text-white px-4 py-2 rounded-xl font-semibold text-sm
              hover:bg-blue-600 active:scale-[0.98] transition-all"
          >
            Apply
          </button>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center
              transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search categories..."
              className="w-full bg-gray-100 rounded-xl px-3 py-2.5 pl-9 text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white
                transition-all"
            />
          </div>
        </div>

        {/* Category tree */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              No categories found
            </div>
          ) : (
            filtered.map(node => renderNode(node))
          )}
        </div>

        {/* Attributes section */}
        {selectedWithAttrs.length > 0 && (
          <div className="border-t border-gray-100">
            <button
              onClick={() => setShowAttributes(!showAttributes)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold
                text-blue-600 hover:bg-blue-50 transition-colors"
            >
              <span>
                All Filters
                <span className="ml-2 text-gray-400 font-normal">
                  ({selectedWithAttrs.reduce((s, n) => s + (n.attributes?.length || 0), 0)})
                </span>
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showAttributes ? 'rotate-180' : ''}`} />
            </button>

            {showAttributes && (
              <div className="px-4 pb-4 max-h-64 overflow-y-auto space-y-4">
                {selectedWithAttrs.map(node => (
                  <div key={node.id}>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                      {node.name}
                    </p>
                    {node.attributes?.map(attr => renderAttribute(attr))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  )
}

/** Tiny checkmark icon */
function CheckMini() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
